import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ArrowLeft, Heart } from 'lucide-react';

const TermsOfServicePage = () => {
  return (
    <div className="min-h-screen bg-[#F7F5F2]">
      {/* Header */}
      <div className="glass-header border-b border-stone-200 sticky top-0 z-50">
        <div className="content-container">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <Heart className="h-7 w-7 text-[#6B8E7B]" fill="#6B8E7B" />
              <div>
                <span className="text-xl font-bold font-['Outfit'] text-stone-800">NurseCommunity</span>
                <span className="text-xs text-stone-400 block">by Gina Rena</span>
              </div>
            </Link>
            <Link to="/">
              <Button variant="ghost" className="rounded-full">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
            </Link>
          </div>
        </div>
      </div>

      <div className="content-container py-12">
        <Card className="max-w-3xl mx-auto border-stone-200 rounded-2xl">
          <CardContent className="p-8 md:p-12">
            <h1 className="text-3xl font-bold text-stone-800 font-['Outfit'] mb-2">Terms of Service</h1>
            <p className="text-stone-500 mb-8">Last updated: March 29, 2026</p>

            <div className="prose prose-stone max-w-none space-y-6">
              <section>
                <h2 className="text-xl font-semibold text-stone-800 font-['Outfit'] mb-3">1. Acceptance of Terms</h2>
                <p className="text-stone-600 leading-relaxed">
                  By accessing or using NurseCommunity by Gina Rena ("Service"), you agree to be bound by these Terms of 
                  Service. If you do not agree to these terms, please do not use our Service.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-stone-800 font-['Outfit'] mb-3">2. Description of Service</h2>
                <p className="text-stone-600 leading-relaxed">
                  NurseCommunity is an online platform designed for nursing professionals to connect, share experiences, 
                  and support each other. The Service includes forums, direct messaging, profile features, and a premium 
                  subscription for anonymous posting.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-stone-800 font-['Outfit'] mb-3">3. Eligibility</h2>
                <p className="text-stone-600 leading-relaxed">
                  You must be at least 18 years old to use this Service. By using the Service, you represent that you are 
                  a nursing professional or healthcare worker and have the legal capacity to enter into these Terms.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-stone-800 font-['Outfit'] mb-3">4. User Accounts</h2>
                <ul className="list-disc list-inside text-stone-600 space-y-2 ml-4">
                  <li>You are responsible for maintaining the confidentiality of your account credentials</li>
                  <li>You agree to provide accurate and complete information</li>
                  <li>You are responsible for all activities under your account</li>
                  <li>You must notify us immediately of any unauthorized use</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-stone-800 font-['Outfit'] mb-3">5. User Conduct</h2>
                <p className="text-stone-600 leading-relaxed mb-3">You agree NOT to:</p>
                <ul className="list-disc list-inside text-stone-600 space-y-2 ml-4">
                  <li>Share patient-identifiable information (HIPAA/GDPR compliance)</li>
                  <li>Post false, misleading, or defamatory content</li>
                  <li>Harass, bully, or intimidate other users</li>
                  <li>Share medical advice as professional consultation</li>
                  <li>Impersonate others or misrepresent your credentials</li>
                  <li>Upload malicious software or spam</li>
                  <li>Violate any applicable laws or regulations</li>
                  <li>Use the Service for commercial solicitation without permission</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-stone-800 font-['Outfit'] mb-3">6. Premium Subscription</h2>
                <ul className="list-disc list-inside text-stone-600 space-y-2 ml-4">
                  <li>Premium features are available for €5 per month</li>
                  <li>Payment is processed securely through Stripe</li>
                  <li>Subscriptions renew automatically unless cancelled</li>
                  <li>You may cancel at any time; access continues until the end of the billing period</li>
                  <li>Refunds are provided at our discretion</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-stone-800 font-['Outfit'] mb-3">7. Anonymous Posting</h2>
                <p className="text-stone-600 leading-relaxed">
                  Premium members may post anonymously in the "Anonymous Worries" section. While your identity is hidden 
                  from other users, you remain responsible for your content. We reserve the right to remove content that 
                  violates these Terms and may disclose user information if required by law.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-stone-800 font-['Outfit'] mb-3">8. Content Ownership</h2>
                <p className="text-stone-600 leading-relaxed">
                  You retain ownership of content you post. By posting, you grant us a non-exclusive, worldwide, 
                  royalty-free license to use, display, and distribute your content on our platform. You represent that 
                  you have the right to share any content you post.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-stone-800 font-['Outfit'] mb-3">9. Moderation</h2>
                <p className="text-stone-600 leading-relaxed">
                  We reserve the right to remove content and suspend or terminate accounts that violate these Terms, 
                  without prior notice. Decisions regarding content moderation are at our sole discretion.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-stone-800 font-['Outfit'] mb-3">10. Disclaimer</h2>
                <p className="text-stone-600 leading-relaxed">
                  THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND. Content shared on this platform is 
                  for informational and support purposes only and should not be considered professional medical or legal 
                  advice. Always consult appropriate professionals for specific guidance.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-stone-800 font-['Outfit'] mb-3">11. Limitation of Liability</h2>
                <p className="text-stone-600 leading-relaxed">
                  To the maximum extent permitted by law, we shall not be liable for any indirect, incidental, special, 
                  consequential, or punitive damages arising from your use of the Service.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-stone-800 font-['Outfit'] mb-3">12. Changes to Terms</h2>
                <p className="text-stone-600 leading-relaxed">
                  We may modify these Terms at any time. Continued use of the Service after changes constitutes acceptance 
                  of the modified Terms. We will notify users of significant changes via email or platform notification.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-stone-800 font-['Outfit'] mb-3">13. Termination</h2>
                <p className="text-stone-600 leading-relaxed">
                  We may terminate or suspend your account at any time for violations of these Terms. You may delete your 
                  account at any time. Upon termination, your right to use the Service ceases immediately.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-stone-800 font-['Outfit'] mb-3">14. Governing Law</h2>
                <p className="text-stone-600 leading-relaxed">
                  These Terms shall be governed by and construed in accordance with applicable laws. Any disputes shall 
                  be resolved through good-faith negotiation or appropriate legal channels.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-stone-800 font-['Outfit'] mb-3">15. Contact</h2>
                <p className="text-stone-600 leading-relaxed">
                  For questions about these Terms, please contact us:
                </p>
                <div className="mt-3 p-4 bg-stone-50 rounded-xl">
                  <p className="text-stone-700 font-medium">Gina Rena</p>
                  <p className="text-stone-600">Email: info@ginarena.com</p>
                  <p className="text-stone-600">Website: ginarena.com</p>
                </div>
              </section>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Footer */}
      <footer className="border-t border-stone-200 bg-white py-8">
        <div className="content-container text-center text-stone-500 text-sm">
          <p>© 2026 NurseCommunity by Gina Rena. All rights reserved.</p>
          <div className="flex justify-center gap-4 mt-2">
            <Link to="/privacy" className="hover:text-[#6B8E7B]">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-[#6B8E7B]">Terms of Service</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TermsOfServicePage;
